import React, {createContext, useContext, useMemo} from "react";
import {BizFormApiClient, createBizFormApiClient} from "../../../packages/biz-form";
import EnvService from "../../../core/services/env";

/**
 * Cached BizForm API clients keyed by module (NCR vs app-wide operator bootstrap).
 */
export interface BizFormApiTokens {
    ncrService: BizFormApiClient;
    operatorApp: BizFormApiClient;
}

const BizFormApiResolverContext = createContext<BizFormApiTokens | null>(null);

function buildBizFormApiTokens(): BizFormApiTokens {
    const ncrUrl = EnvService.BizFormNcrApiUrl;
    const opUrl = EnvService.BizFormOperatorAppApiUrl;
    if (ncrUrl === opUrl) {
        const client = createBizFormApiClient('operatorApp', ncrUrl || undefined);
        return {ncrService: client, operatorApp: client};
    }
    return {
        ncrService: createBizFormApiClient('ncrService', ncrUrl || undefined),
        operatorApp: createBizFormApiClient('operatorApp', opUrl || undefined),
    };
}

/**
 * Provides memoized {@link BizFormApiTokens} for the app (one pair per page load).
 */
export const BizFormApiResolverProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const tokens = useMemo(() => buildBizFormApiTokens(), []);
    return (
        <BizFormApiResolverContext.Provider value={tokens}>
            {children}
        </BizFormApiResolverContext.Provider>
    );
};

/**
 * Selects a BizForm client from {@link BizFormApiTokens} (e.g. {@code t => t.ncrService}).
 */
export function useBizFormApiResolver<T>(select: (tokens: BizFormApiTokens) => T): T {
    const tokens = useContext(BizFormApiResolverContext);
    if (!tokens) {
        throw new Error("useBizFormApiResolver must be used within BizFormApiResolverProvider");
    }
    return select(tokens);
}
